import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import logoSrc from '../assets/images/logo.svg';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate(); // To handle navigation programmatically
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const solutionsDropdownRef = useRef(null);
  const useCasesDropdownRef = useRef(null);

  const linkBaseClasses = "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150";
  const activeLinkClasses = "text-brand-purple font-semibold";
  const inactiveLinkClasses = "text-white hover:text-brand-purple";

  const isAlwaysBlack =
    location.pathname.startsWith('/use-cases') ||
    location.pathname.startsWith('/contact-us') ||
    location.pathname === '/request-demo' ||
    location.pathname.startsWith('/solutions');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // This hook now closes the menu on back/forward navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const shouldBeBlack = isAlwaysBlack || isScrolled;
  const navbarClasses = `fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-28 shadow-md transition-colors duration-300 ${shouldBeBlack ? 'bg-black' : 'bg-transparent backdrop-blur-lg'}`;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenDropdown(null);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdown) => {
    if (window.innerWidth >= 1024) {
      setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    }
  };

  /**
   * FIX: This function handles mobile link clicks directly.
   * It navigates first, then closes the menu to prevent any conflicts.
   */
  const handleMobileLinkClick = (path) => {
    navigate(path);
    closeMobileMenu();
  };

  const mobileLinkBaseClasses = "block px-3 py-2 rounded-md text-base font-medium transition-colors duration-150";

  const solutionsLinks = [
    { to: '/solutions#bpmn', label: 'BloxBlueprint' },
    { to: '/solutions#bpsc', label: 'BPSC Compiler' },
    { to: '/solutions#frontierbox', label: 'FrontierBlox Engine' },
    { to: '/solutions#adapters', label: 'Universal Adapters' },
  ];

  const useCasesLinks = [
    { to: '/use-cases#financial', label: 'Innovative Financial Solutions' },
    { to: '/use-cases#supplychain', label: 'Supply Chain' },
    { to: '/use-cases#healthcare', label: 'Healthcare' },
    { to: '/use-cases#government', label: 'Government' },
  ];

  const renderDropdown = (title, links, ref) => (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`${linkBaseClasses} ${openDropdown === title ? activeLinkClasses : inactiveLinkClasses} flex items-center`}
        onClick={() => toggleDropdown(title)}
      >
        {title}
        <svg className="ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {openDropdown === title && (
        <div className="absolute -left-8 mt-2 w-56 rounded-md shadow-lg bg-black ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpenDropdown(null)}
                className="block px-4 py-2 text-sm text-white hover:bg-gray-800 hover:text-brand-purple"
                role="menuitem"
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <nav className={navbarClasses}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
            <img className="h-10 w-auto" src={logoSrc} alt="ZenithBlox Logo" />
          </Link>

          <div className="hidden lg:flex items-center space-x-4">
            <NavLink to="/" className={({ isActive }) => `${linkBaseClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`}>Home</NavLink>
            <NavLink to="/about-us" className={({ isActive }) => `${linkBaseClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`}>About Us</NavLink>
            {renderDropdown('Solutions', solutionsLinks, solutionsDropdownRef)}
            {renderDropdown('Use Cases', useCasesLinks, useCasesDropdownRef)}
            <NavLink to="/contact-us" className={({ isActive }) => `${linkBaseClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`}>Contact Us</NavLink>
          </div>

          <div className="hidden lg:block">
            <NavLink
              to="/request-demo"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 border-2 border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-[#3D29A1] hover:text-white hover:bg-transparent hover:border-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500"
            >
              Request Demo
            </NavLink>
          </div>

          <div className="lg:hidden flex items-center">
            <button onClick={toggleMobileMenu} type="button" className="text-white hover:text-brand-purple focus:outline-none p-2" aria-controls="mobile-menu" aria-expanded={isMobileMenuOpen}>
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className={`lg:hidden absolute top-full left-0 right-0 w-full shadow-lg z-50 ${shouldBeBlack ? 'bg-black' : 'bg-gray-900 bg-opacity-90 backdrop-blur-md'}`} id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <button onClick={() => handleMobileLinkClick('/')} className={`${mobileLinkBaseClasses} w-full text-left ${location.pathname === '/' ? activeLinkClasses : inactiveLinkClasses}`}>Home</button>
            <button onClick={() => handleMobileLinkClick('/about-us')} className={`${mobileLinkBaseClasses} w-full text-left ${location.pathname === '/about-us' ? activeLinkClasses : inactiveLinkClasses}`}>About Us</button>

            {/* Mobile Solutions Dropdown */}
            <div>
              <button
                onClick={() => setOpenDropdown(openDropdown === 'Solutions' ? null : 'Solutions')}
                className={`${mobileLinkBaseClasses} w-full text-left flex justify-between items-center ${inactiveLinkClasses}`}
              >
                Solutions
                <svg className={`h-5 w-5 transform transition-transform ${openDropdown === 'Solutions' ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
              {openDropdown === 'Solutions' && (
                <div className="pl-4 mt-1 space-y-1">
                  {solutionsLinks.map(link => (
                    <button
                      key={link.to}
                      onClick={() => handleMobileLinkClick(link.to)}
                      className={`${mobileLinkBaseClasses} w-full text-left text-white hover:text-brand-purple`}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Use Cases Dropdown */}
            <div>
              <button
                onClick={() => setOpenDropdown(openDropdown === 'Use Cases' ? null : 'Use Cases')}
                className={`${mobileLinkBaseClasses} w-full text-left flex justify-between items-center ${inactiveLinkClasses}`}
              >
                Use Cases
                <svg className={`h-5 w-5 transform transition-transform ${openDropdown === 'Use Cases' ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
              {openDropdown === 'Use Cases' && (
                <div className="pl-4 mt-1 space-y-1">
                  {useCasesLinks.map(link => (
                    <button
                      key={link.to}
                      onClick={() => handleMobileLinkClick(link.to)}
                      className={`${mobileLinkBaseClasses} w-full text-left text-white hover:text-brand-purple`}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => handleMobileLinkClick('/contact-us')} className={`${mobileLinkBaseClasses} w-full text-left ${location.pathname === '/contact-us' ? activeLinkClasses : inactiveLinkClasses}`}>Contact Us</button>
            <button onClick={() => handleMobileLinkClick('/request-demo')} className="block w-full text-center mt-2 px-6 py-2 border-2 border-[#3D29A1] text-base font-medium rounded-md shadow-sm text-white bg-[#3D29A1] hover:text-white hover:bg-transparent hover:border-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500">Request Demo</button>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;